import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './Contact.css';

const contactInfo = [
  { icon: '📧', label: 'Email', value: 'hello@vistaraklyqs.com', href: 'mailto:hello@vistaraklyqs.com' },
  { icon: '📞', label: 'Phone', value: '+91 9000 000 000', href: 'tel:+919000000000' },
  { icon: '📍', label: 'Location', value: 'Hyderabad, Telangana, India', href: '#' },
  { icon: '⏰', label: 'Business Hours', value: 'Mon–Fri: 9AM – 6PM IST', href: '#' },
];

const services = [
  'AI Agents', 'Chatbot Development', 'Web Application',
  'Mobile App', 'Healthcare Solution', 'Insurance Platform',
  'Cloud & DevOps', 'Other',
];

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', company: '', service: '', budget: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('active'); }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.reveal, .reveal-left, .reveal-right').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setLoading(true);
    try {
      await axios.post('/api/contact', form);
      toast.success('Message sent! We\'ll be in touch within 24 hours.');
      setSent(true);
      setForm({ name: '', email: '', company: '', service: '', budget: '', message: '' });
    } catch (err) {
      toast.error('Failed to send message. Please try again or email us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Hero */}
      <section className="page-hero">
        <div className="page-hero-bg"></div>
        <div className="section-wrapper page-hero-inner">
          <span className="section-tag reveal">Get in Touch</span>
          <h1 className="section-title reveal delay-100">
            Let's Build Something <span>Great Together</span>
          </h1>
          <p className="section-subtitle reveal delay-200">
            Have a project in mind? We'd love to hear about it. Tell us what you're building
            and we'll get back to you within 24 hours.
          </p>
        </div>
      </section>

      {/* Contact Grid */}
      <section className="contact-section">
        <div className="section-wrapper contact-grid">
          {/* Info */}
          <div className="contact-info reveal-left">
            <h2 className="contact-info-title">Contact <span>Information</span></h2>
            <p className="contact-info-sub">
              Reach out through any channel — we're always happy to discuss new ideas, answer questions, or help scope a project.
            </p>
            <div className="contact-cards">
              {contactInfo.map((c, i) => (
                <a key={i} href={c.href} className="contact-info-card glass-card">
                  <span className="ci-icon">{c.icon}</span>
                  <div>
                    <span className="ci-label">{c.label}</span>
                    <span className="ci-value">{c.value}</span>
                  </div>
                </a>
              ))}
            </div>
            <div className="contact-socials">
              <span className="socials-label">Follow us</span>
              <div className="social-links">
                {['LinkedIn', 'Twitter', 'GitHub'].map((s) => (
                  <a key={s} href="#" className="social-btn glass-card">{s}</a>
                ))}
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="contact-form-wrap reveal-right">
            <div className="contact-form-card glass-card">
              {sent ? (
                <div className="form-success">
                  <div className="success-icon">✅</div>
                  <h3>Message Received!</h3>
                  <p>Thank you for reaching out. Our team will contact you within 24 business hours.</p>
                  <button className="btn-primary" onClick={() => setSent(false)} style={{ marginTop: '1.5rem' }}>
                    Send Another
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  <h3 className="form-title">Send us a Message</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Full Name <span className="required">*</span></label>
                      <input
                        type="text" name="name" placeholder="John Doe"
                        value={form.name} onChange={handleChange} required
                      />
                    </div>
                    <div className="form-group">
                      <label>Email Address <span className="required">*</span></label>
                      <input
                        type="email" name="email" placeholder="john@company.com"
                        value={form.email} onChange={handleChange} required
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Company</label>
                      <input
                        type="text" name="company" placeholder="Your company"
                        value={form.company} onChange={handleChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Service Interested In</label>
                      <select name="service" value={form.service} onChange={handleChange}>
                        <option value="">Select a service</option>
                        {services.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Estimated Budget</label>
                    <select name="budget" value={form.budget} onChange={handleChange}>
                      <option value="">Select range</option>
                      <option value="<10k">Under $10,000</option>
                      <option value="10k-50k">$10,000 – $50,000</option>
                      <option value="50k-100k">$50,000 – $100,000</option>
                      <option value=">100k">$100,000+</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Message <span className="required">*</span></label>
                    <textarea
                      name="message" rows="5"
                      placeholder="Tell us about your project, goals, and timeline..."
                      value={form.message} onChange={handleChange} required
                    ></textarea>
                  </div>
                  <button type="submit" className="btn-primary submit-btn" disabled={loading}>
                    {loading ? <><span className="spinner"></span> Sending...</> : 'Send Message →'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <div className="section-wrapper">
          <div className="section-header reveal">
            <span className="section-tag">FAQ</span>
            <h2 className="section-title">Common <span>Questions</span></h2>
          </div>
          <div className="faq-grid">
            {[
              { q: 'How quickly can you start?', a: 'Most projects kick off within 1–2 weeks after our initial discovery call and contract signing.' },
              { q: 'Do you work with startups?', a: 'Absolutely. We love early-stage startups and offer flexible engagement models to match tight budgets.' },
              { q: 'What engagement models do you offer?', a: 'Fixed-price projects, time-and-material, dedicated team, and retainer-based models are all available.' },
              { q: 'Do you provide post-launch support?', a: 'Yes — every project includes a maintenance SLA option with monitoring, bug fixes, and feature iterations.' },
            ].map((f, i) => (
              <div key={i} className={`faq-card glass-card reveal delay-${i * 100 + 100}`}>
                <h4 className="faq-q">{f.q}</h4>
                <p className="faq-a">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
